import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Stable sort of URLSearchParams entries, then stringify.
 */
function stringifySortedParams(sp) {
    const entries = [...sp.entries()].sort(([a], [b]) => a.localeCompare(b));
    return new URLSearchParams(entries).toString();
}

/**
 * Build a query string from state, omitting defaults and eliding "empty" values.
 * Defaults: { page=1, size=10, sortBy='id', sortDir='asc', filters?: object }
 */
export function buildQueryString(state, defaults) {
    const p = new URLSearchParams();
    const omit = (k, v) =>
        v == null ||
        v === '' ||
        v === 'All' ||
        (k === 'page' && Number(v) === (defaults.page ?? 1)) ||
        (k === 'size' && Number(v) === (defaults.size ?? 10)) ||
        (k === 'sortBy' && String(v) === (defaults.sortBy ?? 'id')) ||
        (k === 'sortDir' && String(v) === (defaults.sortDir ?? 'asc'));

    Object.entries(state).forEach(([k, v]) => {
        if (!omit(k, v)) p.set(k, String(v));
    });

    return stringifySortedParams(p);
}

/**
 * Initial state hydrated from the URL (client) or defaults (SSR).
 */
export function initialFromUrl(defaults) {
    const base = {
        page: defaults.page ?? 1,
        size: defaults.size ?? 10,
        sortBy: defaults.sortBy ?? 'id',
        sortDir: defaults.sortDir ?? 'asc',
        q: '',
        ...(defaults.filters || {}),
    };

    if (typeof window === 'undefined') return base;

    const params = new URLSearchParams(window.location.search);
    const core = {
        page: Number(params.get('page') || base.page),
        size: Number(params.get('size') || base.size),
        sortBy: params.get('sortBy') || base.sortBy,
        sortDir: params.get('sortDir') || base.sortDir,
        q: params.get('q') || '',
    };

    // Merge in any additional params (treated as filters).
    const extras = Object.fromEntries(
        [...params.entries()].filter(([k]) => !['page', 'size', 'sortBy', 'sortDir', 'q'].includes(k))
    );

    return { ...base, ...extras, ...core };
}

/**
 * Build a stable "filter key" out of all non-control fields in state.
 * Control fields are not part of filtering: page/size/sortBy/sortDir/q.
 * We include ALL other keys, sorted by key name, to avoid collisions as filters expand.
 */
function buildFilterKey(state) {
    const CONTROL = new Set(['page', 'size', 'sortBy', 'sortDir', 'q']);
    const entries = Object.entries(state)
        .filter(([k]) => !CONTROL.has(k))
        .map(([k, v]) => [k, v ?? 'All']);

    entries.sort(([a], [b]) => a.localeCompare(b));
    return entries.map(([k, v]) => `${k}=${v}`).join('&') || '__NO_FILTERS__';
}

/**
 * Debounce any value change.
 */
function useDebouncedValue(value, ms) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return debounced;
}

/**
 * Keep the URL search in sync with a normalized query string.
 * Uses replaceState (not pushState) to avoid polluting history.
 */
function useUrlSync(qs, enabled, ms) {
    const lastWrittenQsRef = useRef('');
    const debounceRef = useRef(0);

    useEffect(() => {
        if (!enabled || typeof window === 'undefined') return;

        const norm = (sp) => stringifySortedParams(sp);
        const nowNorm = norm(new URLSearchParams(window.location.search));

        if (qs === nowNorm || qs === lastWrittenQsRef.current) return;

        clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => {
            const currentNorm = norm(new URLSearchParams(window.location.search));
            if (qs !== currentNorm) {
                const nextUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
                window.history.replaceState(null, '', nextUrl);
                lastWrittenQsRef.current = qs;
            }
        }, ms);

        return () => clearTimeout(debounceRef.current);
    }, [qs, enabled, ms]);
}

/**
 * Main hook
 *
 * @param {Object} options
 * @param {Object} options.defaults - { page, size, sortBy, sortDir, filters?: object }
 * @param {(params: {page:number,size:number,sortBy:string,sortDir:'asc'|'desc',q:string, ...filters}) => Promise<{rows:any[], total:number}>} options.fetcher
 * @param {boolean} [options.syncToUrl=true]
 * @param {number} [options.searchDebounceMs=250]
 * @param {number} [options.urlDebounceMs=200]
 */
export function useListQuery({
    defaults,
    fetcher,
    syncToUrl = true,
    searchDebounceMs = 250,
    urlDebounceMs = 200,
}) {
    // State & derived values
    const [state, setState] = useState(() => initialFromUrl(defaults));
    const debouncedQ = useDebouncedValue(state.q, searchDebounceMs);

    const [data, setData] = useState({ rows: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Remember last page per *filter combination*
    const pageMemoryRef = useRef(new Map([[buildFilterKey(state), state.page]]));

    // Keep latest fetcher ref
    const fetchRef = useRef(fetcher);
    useEffect(() => {
        fetchRef.current = fetcher;
    }, [fetcher]);

    // Build fetch params in one place
    const params = useMemo(() => {
        const { page, size, sortBy, sortDir, q, ...filters } = state;
        return { page, size, sortBy, sortDir, q: debouncedQ, ...filters };
    }, [state, debouncedQ]);

    // Data fetching
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');

        fetchRef
            .current(params)
            .then((d) => {
                if (!cancelled) setData(d || { rows: [], total: 0 });
            })
            .catch((e) => {
                if (!cancelled) setError(e?.message || 'Failed');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [params]);

    // URL sync
    const qs = useMemo(() => buildQueryString(state, defaults), [state, defaults]);
    useUrlSync(qs, syncToUrl, urlDebounceMs);

    // Updaters
    const setPage = (page) =>
        setState((s) => {
            if (s.page === page) return s;
            const next = { ...s, page };
            pageMemoryRef.current.set(buildFilterKey(next), page);
            return next;
        });

    const setSort = (sortBy) =>
        setState((s) => {
            const isSame = s.sortBy === sortBy;
            const sortDir = isSame ? (s.sortDir === 'asc' ? 'desc' : 'asc') : 'asc'; // default to ASC on first click
            const next = { ...s, sortBy, sortDir, page: 1 };
            pageMemoryRef.current.set(buildFilterKey(next), 1);
            return next;
        });

    /**
     * Set a filter field. Restores remembered page for the resulting filter combo.
     * If none remembered, falls back to 1 (standard list UX).
     */
    const setFilter = (k, v) =>
        setState((s) => {
            if (s[k] === v) return s;
            const next = { ...s, [k]: v };
            const key = buildFilterKey(next);
            const remembered = pageMemoryRef.current.get(key) ?? 1;
            next.page = remembered;
            pageMemoryRef.current.set(key, remembered);
            return next;
        });

    const setSearch = (q) =>
        setState((s) => {
            if (s.q === q) return s;
            return { ...s, q, page: 1 };
        });

    const refresh = () => {
        setState((s) => ({ ...s }));
    };

    return { state, data, loading, error, setPage, setSort, setFilter, setSearch, refresh };
}

export default useListQuery;
